/* VoicePlayer — premium audio player for Voice Memories (iter67).
   Decorative waveform (seeded per recording) doubles as a seek bar. */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Play, Pause } from 'lucide-react';
import { registerAudioSource, claimAudioFocus } from '../lib/audioBus';

let __voice_player_seq = 0;

function fmt(secs) {
  if (!Number.isFinite(secs) || secs < 0) return '0:00';
  const m = Math.floor(secs / 60);
  const s = Math.floor(secs % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

// Deterministic pseudo-random bars so each recording has its own "waveform".
function seededBars(seed, count = 44) {
  let h = 2166136261;
  for (let i = 0; i < seed.length; i++) {
    h ^= seed.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  const bars = [];
  for (let i = 0; i < count; i++) {
    h ^= h << 13; h ^= h >>> 17; h ^= h << 5;
    const v = ((h >>> 0) % 1000) / 1000;
    bars.push(0.28 + 0.72 * (0.4 * v + 0.6 * Math.abs(Math.sin((i / count) * Math.PI * 2.2 + v * 3))));
  }
  return bars;
}

export function VoicePlayer({ src, seed = '', durationHint = null, accent = 'hsl(var(--accent))', base = 'hsl(var(--primary) / 0.18)', testid }) {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [current, setCurrent] = useState(0);
  const [duration, setDuration] = useState(durationHint || 0);
  const bars = useMemo(() => seededBars(seed || src || 'voice'), [seed, src]);
  const busIdRef = useRef(null);
  if (!busIdRef.current) busIdRef.current = `voice-${++__voice_player_seq}`;

  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    const onTime = () => setCurrent(a.currentTime || 0);
    const onMeta = () => { if (Number.isFinite(a.duration) && a.duration > 0) setDuration(a.duration); };
    const onEnd = () => { setPlaying(false); setCurrent(0); };
    const onPlay = () => { setPlaying(true); claimAudioFocus(busIdRef.current); };
    a.addEventListener('timeupdate', onTime);
    a.addEventListener('loadedmetadata', onMeta);
    a.addEventListener('durationchange', onMeta);
    a.addEventListener('ended', onEnd);
    a.addEventListener('pause', () => setPlaying(false));
    a.addEventListener('play', onPlay);
    const unregister = registerAudioSource(busIdRef.current, () => { if (!a.paused) a.pause(); });
    return () => {
      a.removeEventListener('timeupdate', onTime);
      a.removeEventListener('loadedmetadata', onMeta);
      a.removeEventListener('durationchange', onMeta);
      a.removeEventListener('ended', onEnd);
      a.removeEventListener('play', onPlay);
      unregister();
    };
  }, [src]);

  const toggle = () => {
    const a = audioRef.current;
    if (!a) return;
    if (a.paused) a.play().catch(() => {});
    else a.pause();
  };

  const seek = (e) => {
    const a = audioRef.current;
    if (!a || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const frac = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
    a.currentTime = frac * duration;
    setCurrent(a.currentTime);
  };

  const progress = duration ? current / duration : 0;

  return (
    <div className="flex items-center gap-3 w-full" data-testid={testid}>
      <audio ref={audioRef} preload="none" src={src} />
      <button
        type="button"
        onClick={toggle}
        aria-label={playing ? 'Pause' : 'Play'}
        className="w-11 h-11 rounded-full flex items-center justify-center shrink-0 transition-transform hover:scale-105"
        style={{ backgroundColor: 'hsl(var(--primary))', color: 'hsl(var(--background))' }}
        data-testid={testid ? `${testid}-toggle` : undefined}
      >
        {playing ? <Pause className="w-4 h-4" fill="currentColor" /> : <Play className="w-4 h-4 ml-0.5" fill="currentColor" />}
      </button>

      <div
        className="flex-1 flex items-center gap-[2px] h-11 cursor-pointer"
        onClick={seek}
        role="slider"
        aria-label="Seek"
        aria-valuemin={0}
        aria-valuemax={Math.round(duration)}
        aria-valuenow={Math.round(current)}
      >
        {bars.map((b, i) => {
          const filled = (i + 0.5) / bars.length <= progress;
          return (
            <span
              key={i}
              className="flex-1 rounded-full transition-colors duration-150"
              style={{
                height: `${Math.round(b * 100)}%`,
                minWidth: 2,
                backgroundColor: filled ? accent : base,
              }}
            />
          );
        })}
      </div>

      <span className="text-[11px] font-mono shrink-0 tabular-nums" style={{ color: 'hsl(var(--primary) / 0.65)' }}>
        {playing || current > 0 ? `${fmt(current)} / ` : ''}{fmt(duration)}
      </span>
    </div>
  );
}

export default VoicePlayer;
